import type { Device } from '@capacitor/device'
import type { Preferences } from '@capacitor/preferences'
import type { Capacitor } from '@capacitor/core'
import { useLogger } from '../composables/useLogger'
import { useI18n } from 'vue-i18n'
import { useCookie } from '#app'
import { useConfigStore } from '../stores/useConfigStore'
import { useAuthStore } from '../stores/authStore'
import { watch } from 'vue'

/**
 * Key used to store the user's language preference in Capacitor's Preferences storage
 * In web environments, this will be prefixed with 'CapacitorStorage.'
 */
const LANGUAGE_KEY = 'userLanguage'

export default defineNuxtPlugin(async (nuxtApp) => {
    const logger = useLogger()
    logger.debug('i18n: Initializing i18n plugin')

    // Initialize i18n and verify it's ready
    await nuxtApp.vueApp._context.provides.$i18n?.ready
    logger.debug('i18n: i18n is ready')

    const i18n = useNuxtApp().$i18n
    const authStore = useAuthStore()
    const configStore = useConfigStore()
    
    // Flag to track if one-time migration has been attempted
    let migrationAttempted = false
    
    // Early return if i18n initialization failed
    if (!i18n) {
        logger.error('i18n: i18n is not initialized')
        return {
            provide: {
                setLanguage: async () => false,
                getStoredLanguage: async () => null
            }
        }
    }

    // Capacitor module references - initialized only on client side
    let device: typeof Device
    let preferences: typeof Preferences
    let capacitor: typeof Capacitor

    /**
     * Initialize Capacitor modules on client side only
     * These are dynamically imported to avoid SSR issues
     */
    if (process.client) {
        logger.debug('i18n: Loading Capacitor modules')
        const cap = await import('@capacitor/core')
        const dev = await import('@capacitor/device')
        const pref = await import('@capacitor/preferences')
        
        capacitor = cap.Capacitor
        device = dev.Device
        preferences = pref.Preferences
        logger.debug('i18n: Capacitor modules loaded')

        logger.debug('i18n: Current CapacitorStorage value:', 
            localStorage.getItem('CapacitorStorage.userLanguage'))
    }

    /**
     * Checks if the user is authenticated
     * @returns boolean True if user is authenticated
     */
    function isAuthenticated(): boolean {
        return !!authStore.userId
    }

    /**
     * Checks and migrates language from i18n_redirected cookie if present
     * This is a temporary migration to handle legacy cookies
     * Should be removed once all users have migrated
     * @returns Promise<boolean> True if migration was performed
     */
    async function migrateFromRedirectCookie(): Promise<boolean> {
        if (!process.client) return false

        const redirectCookie = useCookie('i18n_redirected')
        const cookieValue = redirectCookie.value

        if (cookieValue && (cookieValue === 'da' || cookieValue === 'en')) {
            logger.debug(`i18n: Found redirect cookie with value: ${cookieValue}`)
            
            try {
                // Use existing setLanguage function to handle storage and locale setting
                const success = await setLanguage(cookieValue)
                
                if (success) {
                    // Delete the cookie only if migration was successful
                    redirectCookie.value = null
                    logger.debug('i18n: Deleted redirect cookie after migration')
                    return true
                }
            } catch (error) {
                logger.logErrorCaught('i18n: Error during cookie migration:', 'I18nInit.ts', error)
            }
        }
        
        return false
    }

    /**
     * Gets the device's language code using Capacitor's Device API
     * Only works in client-side native environments
     * @returns Promise<string> The device language code or empty string if unavailable
     */
    async function getDeviceLanguage(): Promise<string> {
        if (!process.client || !device) {
            logger.debug('i18n: Cannot get device language - not in client or no device')
            return ''
        }

        try {
            const { value } = await device.getLanguageCode()
            logger.debug(`i18n: Got device language: ${value}`)
            return value
        } catch (error) {
            logger.logErrorCaught('i18n: Error getting device language:', 'I18nInit.ts', error)
            return ''
        }
    }

    /**
     * Gets the browser's language preference
     * Only works in client-side web environments
     * @returns string The browser's language code or empty string if unavailable
     */
    function getBrowserLanguage(): string {
        if (!process.client) return ''
        const lang = navigator.language || navigator.languages[0]
        logger.debug(`i18n: Got browser language: ${lang}`)
        return lang
    }

    /**
     * Checks if a language exists in customer configuration
     * @returns Promise<string|null> Language from customer config or null
     */
    async function getLanguageFromCustomerConfig(): Promise<string | null> {
        if (!isAuthenticated() || !configStore.userConfiguration) {
            return null
        }
        
        try {
            const configLang = configStore.userConfiguration.get<string>('language.preferredLocale')
            logger.debug(`i18n: Got language from customer config: ${configLang}`)
            return configLang || null
        } catch (error) {
            logger.logErrorCaught('i18n: Error getting language from customer config:', 'I18nInit.ts', error)
            return null
        }
    }

    /**
     * Gets language from local preferences
     * @returns Promise<string|null> Language from preferences or null
     */
    async function getLanguageFromPreferences(): Promise<string | null> {
        if (!process.client || !preferences) {
            return null
        }

        try {
            const { value } = await preferences.get({ key: LANGUAGE_KEY })
            logger.debug(`i18n: Got language from preferences: ${value}`)
            return value || null
        } catch (error) {
            logger.logErrorCaught('i18n: Error getting language from preferences:', 'I18nInit.ts', error)
            return null
        }
    }

    /**
     * Migrates language from preferences to customer config if needed
     * Only happens if user is authenticated, customer config exists, and has no language setting
     * @returns Promise<boolean> True if migration was performed
     */
    async function migratePreferencesToCustomerConfig(): Promise<boolean> {
        if (migrationAttempted || !isAuthenticated()) {
            return false
        }

        migrationAttempted = true
        
        try {
            // Wait for customer configuration to be loaded
            if (!configStore.userConfiguration) {
                await configStore.loadCustomerConfiguration()
            }

            // Check if customer config already has language setting
            const configLang = await getLanguageFromCustomerConfig()
            if (configLang) {
                logger.debug(`i18n: No migration needed - customer config already has language: ${configLang}`)
                return false
            }
            
            // Get language from preferences
            const prefsLang = await getLanguageFromPreferences()
            if (!prefsLang) {
                logger.debug('i18n: No language in preferences to migrate')
                return false
            }
            
            // Migrate language from preferences to customer config
            logger.debug(`i18n: Migrating language from preferences to customer config: ${prefsLang}`)
            await configStore.updateValue('language.preferredLocale', prefsLang)
            return true
        } catch (error) {
            logger.logErrorCaught('i18n: Error during preferences migration:', 'I18nInit.ts', error)
            return false
        }
    }

    /**
     * Main function to detect and set the application language
     * Priority: Customer config > Stored preference > Device language > Browser language > Default locale
     * Also handles storing the detected language for future use
     */
    async function detectAndSetLanguage() {
        logger.debug('i18n: Starting language detection')
        
        let detectedLocale = await getStoredLanguage()
        
        logger.debug(`i18n: Stored language found: ${detectedLocale}`)

        if (!detectedLocale) {
            if (process.client && capacitor?.isNativePlatform()) {
                logger.debug('i18n: Getting language from device')
                const deviceLang = await getDeviceLanguage()
                detectedLocale = mapDeviceLanguageToLocale(deviceLang, i18n.locales.value)
            } else {
                logger.debug('i18n: Getting language from browser')
                const browserLang = getBrowserLanguage()
                detectedLocale = mapBrowserLanguageToLocale(browserLang, i18n.locales.value)
            }
            logger.debug(`i18n: Detected locale: ${detectedLocale}`)

            // Store the detected locale in appropriate storage
            if (detectedLocale) {
                await setLanguage(detectedLocale)
            }
        }

        if (!detectedLocale || !i18n.locales.value.some(loc => loc.code === detectedLocale)) {
            logger.debug(`i18n: Using default locale: ${i18n.defaultLocale}`)
            detectedLocale = i18n.defaultLocale
            
            // Store the default locale if no valid locale was found
            await setLanguage(detectedLocale)
        }
        
        if (detectedLocale) {
            logger.debug(`i18n: Setting locale to: ${detectedLocale}`)
            await i18n.setLocale(detectedLocale)
            
            if (process.client) {
                logger.debug(`i18n: Active i18n locale: ${i18n.locale.value}`)
                logger.debug(`i18n: Document lang attribute: ${document.documentElement.lang}`)
            }
        }
    }

    /**
     * Maps a device language code to a supported locale
     * @param deviceLang The device language code (e.g., "en-US")
     * @param supportedLocales Array of supported locales from i18n configuration
     * @returns string|null The matched locale code or null if no match
     */
    function mapDeviceLanguageToLocale(deviceLang: string, supportedLocales: any[]): string | null {
        if (!deviceLang) return null
        logger.debug(`i18n: Mapping device language ${deviceLang} to supported locales`, {locales: supportedLocales})
        const langCode = deviceLang.split('-')[0]
        const matchedLocale = supportedLocales.find(locale => locale.code === langCode)
        logger.debug(`i18n: Mapped device language ${deviceLang} to locale: ${matchedLocale?.code}`)
        return matchedLocale ? matchedLocale.code : null
    }

    /**
     * Maps a browser language code to a supported locale
     * @param browserLang The browser language code (e.g., "en-US")
     * @param supportedLocales Array of supported locales from i18n configuration
     * @returns string|null The matched locale code or null if no match
     */
    function mapBrowserLanguageToLocale(browserLang: string, supportedLocales: any[]): string | null {
        if (!browserLang) return null
        logger.debug(`i18n: Mapping browser language ${browserLang} to supported locales`, {locales: supportedLocales})
        const langCode = browserLang.split('-')[0]
        const matchedLocale = supportedLocales.find(locale => locale.code === langCode)
        logger.debug(`i18n: Mapped browser language ${browserLang} to locale: ${matchedLocale?.code}`)
        return matchedLocale ? matchedLocale.code : null
    }

    /**
     * Retrieves the stored language preference with proper priority
     * For authenticated users: Customer config > Local preferences
     * For non-authenticated users: Local preferences only
     * @returns Promise<string|null> The stored locale code or null if not found
     */
    async function getStoredLanguage(): Promise<string | null> {
        logger.debug('i18n: Getting stored language')
        // Try migration if authenticated
        if (isAuthenticated()) {
            logger.debug('i18n: Migrating preferences to customer config')
            await migratePreferencesToCustomerConfig()
        }
        
        // Priority 1: Check customer configuration if authenticated
        if (isAuthenticated()) {
            logger.debug('i18n: Getting language from customer config')
            const configLang = await getLanguageFromCustomerConfig()
            if (configLang) {
                return configLang
            }
        }

        // Priority 2: Fall back to preferences for all users
        return await getLanguageFromPreferences()
    }

    /**
     * Sets and stores the user's language preference
     * For authenticated users: Updates both customer config and local preferences
     * For non-authenticated users: Updates only local preferences
     * @param locale The locale code to set (must be supported in i18n config)
     * @returns Promise<boolean> Success status of the operation
     */
    async function setLanguage(locale: string): Promise<boolean> {
        logger.debug(`i18n: Attempting to set language to: ${locale}`)
        if (!i18n.locales.value.some(loc => loc.code === locale)) {
            logger.warn(`i18n: Invalid locale: ${locale}`)
            return false
        }

        try {
            // Store in customer configuration if authenticated
            if (isAuthenticated() && configStore.userConfiguration) {
                logger.debug(`i18n: Storing language in customer config: ${locale}`)
                await configStore.updateValue('language.preferredLocale', locale)
            }

            // Always store in preferences (for all users)
            if (process.client && preferences) {
                logger.debug(`i18n: Storing language in preferences: ${locale}`)
                await preferences.set({
                    key: LANGUAGE_KEY,
                    value: locale
                })
            }
            
            await i18n.setLocale(locale)
            logger.debug(`i18n: Successfully set language to: ${locale}`)
            return true
        } catch (error) {
            logger.logErrorCaught('i18n: Error setting language:', 'I18nInit.ts', error)
            return false
        }
    }

    // Watch for authentication changes
    watch(() => authStore.userId, async (newUserId, oldUserId) => {
        if (newUserId && !oldUserId) {
            // User just logged in
            logger.debug('i18n: User logged in, checking if language migration is needed')
            
            // After login, check if we need to migrate preferences to customer config
            await migratePreferencesToCustomerConfig()
            
            // Ensure language is set from customer config
            const configLang = await getLanguageFromCustomerConfig()
            if (configLang) {
                await setLanguage(configLang)
            }
        }
    })

    // Initialize language settings on plugin load
    try {
        // First try to migrate from cookie
        const migrated = await migrateFromRedirectCookie()
        
        if (!migrated) {
            // If no migration performed, proceed with normal detection
            await detectAndSetLanguage()
        }
        
        logger.debug('i18n: Initial language detection and setup complete')
    } catch (error) {
        logger.logErrorCaught('i18n: Error during language detection:', 'I18nInit.ts', error)
    }

    logger.debug('i18n: Plugin initialization complete')
    
    // Provide language management functions to the application
    return {
        provide: {
            setLanguage,
            getStoredLanguage
        }
    }
})